import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useAxiosInstance,
} from './utils';

const URL = 'analytics/paid_by_reminder';

type LoadPaidByReminderVariables = { periodMonths: number };

const AmountSchema = z.preprocess(val => Number(val), z.number());

const SegmentSchema = z.object({
  count: z.number(),
  amount: AmountSchema,
});

const ESCALATION_STAGES = [
  'preventive_reminder',
  'first_reminder',
  'intermediate_reminder',
  'last_reminder',
  'initiate_ai_call',
  'additional_reminder',
  'formal_notice',
  'sent_to_third_party',
] as const;

const EscalationStageSchema = z.literal(ESCALATION_STAGES);

export type EscalationStage = z.infer<typeof EscalationStageSchema>;

const StepSchema = SegmentSchema.extend({
  step: z.number(),
  stepType: EscalationStageSchema.nullable(),
});

export const WorkflowSchema = z.object({
  id: z.number(),
  name: z.string(),
  steps: z.array(StepSchema),
  open: SegmentSchema,
});

export type PaidByReminderWorkflow = z.infer<typeof WorkflowSchema>;

export const PaidByReminderSchema = z.object({
  periodMonths: z.number(),
  population: z.object({ count: z.number(), amount: AmountSchema }),
  steps: z.array(StepSchema),
  open: SegmentSchema,
  workflows: z.array(WorkflowSchema),
});

export type PaidByReminder = z.infer<typeof PaidByReminderSchema>;

async function loadPaidByReminderQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { periodMonths }: LoadPaidByReminderVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params: { period_months: periodMonths } });

  const parseResult = PaidByReminderSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'paidByReminder');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadPaidByReminder({ periodMonths }: LoadPaidByReminderVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<PaidByReminder, ApiError>({
    queryKey: ['paid-by-reminder', periodMonths],
    queryFn: () => loadPaidByReminderQueryFn(axiosInstance, { periodMonths }),
  });

  return addResourceNameToQueryResult<PaidByReminder, ApiError, 'paidByReminder'>(
    'paidByReminder',
    queryResult,
  );
}
